import { addMilliseconds } from "date-fns/addMilliseconds";
import { action, computed, observable, makeObservable } from "mobx";
import { now } from "mobx-utils";
import { type ReactNode } from "react";
import {
  auth0ErrorCode,
  auth0ErrorDescription,
  isAuth0Error,
} from "nvent-web/services/auth0/auth0Error";

export class NotificationsStore {
  all: Notification[] = [];

  constructor() {
    makeObservable(this, {
      all: observable,
      visible: computed,
      push: action,
      create: action,
      createInfo: action,
      createSuccess: action,
      createWarning: action,
      createError: action,
      createGenericError: action,
    });
  }

  get visible(): Notification[] {
    return this.all.filter((n) => n.isVisible);
  }

  push(notification: Notification) {
    this.all.push(notification);
  }

  create(message: ReactNode, options?: NotificationOptions): Notification {
    const notification = new Notification(message, options);
    this.push(notification);

    return notification;
  }

  createInfo(message: ReactNode) {
    return this.create(message, { theme: "info" });
  }

  createSuccess(message: ReactNode) {
    return this.create(message, { theme: "success" });
  }

  createWarning(message: ReactNode) {
    return this.create(message, { theme: "warning" });
  }

  createError(message: ReactNode) {
    return this.create(message, { theme: "danger" });
  }

  createGenericError(error: unknown, message: ReactNode = "Error") {
    if (isAuth0Error(error)) {
      return this.createError(
        <>
          {message} ({auth0ErrorCode(error)}): {auth0ErrorDescription(error)}
        </>
      );
    } else {
      return this.createError(
        <>
          {message}: {(error as { toString: () => string }).toString()}
        </>
      );
    }
  }
}

export interface NotificationOptions {
  theme?: "success" | "danger" | "warning" | "info";
  expiresIn?: number;
  expiresAt?: Date;
}

export class Notification {
  private static nextKey = 0;

  readonly key = Notification.nextKey++;
  readonly message: ReactNode;

  theme = "info";
  isClosed = false;
  expirationDate?: Date;

  get hasExpired() {
    return this.expirationDate && this.expirationDate.getTime() < now();
  }

  get isVisible() {
    return !this.isClosed && !this.hasExpired;
  }

  constructor(
    message: ReactNode,
    { theme = "info", expiresIn = 4000, expiresAt }: NotificationOptions = {}
  ) {
    makeObservable(this, {
      theme: observable,
      isClosed: observable,
      expirationDate: observable,
      hasExpired: computed,
      isVisible: computed,
      close: action,
    });

    this.message = message;
    this.theme = theme;

    if (expiresIn) {
      this.expirationDate = addMilliseconds(new Date(), expiresIn);
    } else if (expiresAt) {
      this.expirationDate = expiresAt;
    }
  }

  close = () => (this.isClosed = true);
}
